"use client";

import { lazy, Suspense, useCallback, useEffect, useState } from "react";

import { useCurrentWebPushCapability } from "@/hooks/use-current-web-push-capability";
import { useDistributionAvailability } from "@/hooks/use-distribution-availability";
import { useMessagingPromptDismissal } from "@/hooks/use-messaging-prompt-dismissal";
import { useMessagingPromptRequest } from "@/hooks/use-messaging-prompt-request";
import { useWebPushInstallIntent } from "@/hooks/use-web-push-install-intent";
import { isCurrentWebPushAvailable } from "@/lib/isCurrentWebPushAvailable";
import { isWebPushJourneyAvailable } from "@/lib/isWebPushJourneyAvailable";
import { notificationPermissionContextKey } from "@/lib/notificationPermissionContextKey";
import {
  resolveNotificationPermissionForContext,
  type NotificationPermissionSnapshot,
} from "@/lib/resolveNotificationPermissionForContext";
import type { ViewerContext } from "@/lib/viewer-context";
import { hasVerifiedAccountEmail } from "@garimpa/lib/hasVerifiedAccountEmail";

import { resolveContextualRequestCompletion } from "./resolveContextualRequestCompletion";
import { resolveMessagingPromptAvailability } from "./resolveMessagingPromptAvailability";

const MessagingPrompt = lazy(() =>
  import("./messaging-prompt").then((module) => ({ default: module.MessagingPrompt })),
);
const BROWSER_PUSH_PROMPT_SCOPE = "browser";
const BROWSER_PUSH_PROMPT_STORAGE_NAMESPACE = "garimpa.browserPushPrompt.dismissedUntil";

function getDismissalScope(viewer: ViewerContext) {
  return viewer.userId ?? "visitor";
}

function getAuthenticatedScope(viewer: ViewerContext) {
  return viewer.isAuthenticated ? viewer.userId : undefined;
}

function getHasVerifiedEmail(viewer: ViewerContext) {
  return viewer.isMember ? hasVerifiedAccountEmail(viewer.user) : false;
}

function resolveWebPushLoading(configLoading: boolean, capabilityResolved: boolean) {
  return [configLoading, !capabilityResolved].some(Boolean);
}

function resolveGeneralWebPushAvailability(
  available: boolean | undefined,
  permission: NotificationPermission | null,
) {
  if (permission === "denied") return false;
  return available;
}

function resolveBrowserPromptReady(input: {
  capabilityResolved: boolean;
  dismissalResolved: boolean;
  installIntentResolved: boolean;
  permissionResolved: boolean;
}) {
  return Object.values(input).every(Boolean);
}

function resolveContextualRequestKey(
  promptRequest: { requestedAt: number } | null,
  installIntent: { requestedAt: number } | null,
) {
  return promptRequest?.requestedAt ?? installIntent?.requestedAt ?? null;
}

function useNotificationPermission(contextKey: string) {
  const [snapshot, setSnapshot] = useState<NotificationPermissionSnapshot>({
    contextKey: null,
    permission: null,
  });

  useEffect(() => {
    setSnapshot({
      contextKey,
      permission: typeof Notification === "undefined" ? null : Notification.permission,
    });
  }, [contextKey]);

  return resolveNotificationPermissionForContext(snapshot, contextKey);
}

function resolveContextualKind(input: {
  browserPermissionDenied: boolean;
  hasInstallIntent: boolean;
  hasPriceAlertRequest: boolean;
}): "install_return" | "price_alert" | null {
  if (input.browserPermissionDenied) return null;
  if (input.hasInstallIntent) return "install_return";
  if (input.hasPriceAlertRequest) return "price_alert";
  return null;
}

function useContextualRequestCleanup({
  browserPermissionDenied,
  browserPromptDismissed,
  completeInstallIntent,
  completeAllContextualRequests,
  completePromptRequest,
  promptRequest,
  webPushJourneyAvailable,
  webPushInstallIntent,
  webPushLoading,
}: {
  browserPermissionDenied: boolean;
  browserPromptDismissed: boolean;
  completeInstallIntent: () => void;
  completeAllContextualRequests: () => void;
  completePromptRequest: () => void;
  promptRequest: unknown;
  webPushJourneyAvailable: boolean | undefined;
  webPushInstallIntent: unknown;
  webPushLoading: boolean;
}) {
  useEffect(() => {
    if (!promptRequest || !browserPromptDismissed) return;
    completePromptRequest();
  }, [browserPromptDismissed, completePromptRequest, promptRequest]);

  useEffect(() => {
    if (!browserPermissionDenied) return;
    completeAllContextualRequests();
  }, [browserPermissionDenied, completeAllContextualRequests]);

  useEffect(() => {
    if (!webPushInstallIntent) return;
    if (webPushLoading || webPushJourneyAvailable !== false) return;
    completeInstallIntent();
  }, [completeInstallIntent, webPushInstallIntent, webPushJourneyAvailable, webPushLoading]);
}

function distributionPromptState(query: ReturnType<typeof useDistributionAvailability>) {
  const telegram = query.data?.telegram;
  return {
    webPushQuery: { data: query.data?.webPush, isLoading: query.isLoading },
    whatsappQuery: { data: query.data?.whatsapp, isLoading: query.isLoading },
    telegramAuthAvailable: [
      telegram?.available === true,
      telegram?.connectionStatus === "resolved",
    ].every(Boolean),
    telegramConnected: telegram?.connection?.connected,
    telegramLoading: [!query.data, query.isLoading].some(Boolean),
  };
}

export function MessagingPromptGate({
  allowGeneralPrompt = true,
  hideGeneralOnMobile = false,
  viewer,
}: {
  allowGeneralPrompt?: boolean;
  hideGeneralOnMobile?: boolean;
  viewer: ViewerContext;
}) {
  const { dismissed, dismissForDays, isResolved } = useMessagingPromptDismissal(
    getDismissalScope(viewer),
  );
  const browserPromptDismissal = useMessagingPromptDismissal(
    BROWSER_PUSH_PROMPT_SCOPE,
    BROWSER_PUSH_PROMPT_STORAGE_NAMESPACE,
  );
  const currentWebPushCapability = useCurrentWebPushCapability();
  const authenticatedScope = getAuthenticatedScope(viewer);
  const { complete: completePromptRequest, request: promptRequest } =
    useMessagingPromptRequest(authenticatedScope);
  const {
    complete: completeInstallIntent,
    intent: webPushInstallIntent,
    resolved: installIntentResolved,
  } = useWebPushInstallIntent();
  const permissionContextKey = notificationPermissionContextKey({
    installRequestedAt: webPushInstallIntent?.requestedAt,
    priceAlertRequestedAt: promptRequest?.requestedAt,
  });
  const notificationPermission = useNotificationPermission(permissionContextKey);
  const { webPushQuery, whatsappQuery, telegramAuthAvailable, telegramConnected, telegramLoading } =
    distributionPromptState(useDistributionAvailability());
  const capabilityResolved = currentWebPushCapability !== null;
  const webPushLoading = resolveWebPushLoading(webPushQuery.isLoading, capabilityResolved);
  const webPushAvailable = isCurrentWebPushAvailable(currentWebPushCapability, webPushQuery.data);
  const webPushJourneyAvailable = isWebPushJourneyAvailable(
    currentWebPushCapability,
    webPushQuery.data,
  );
  const hasVerifiedEmail = getHasVerifiedEmail(viewer);
  const browserPermissionDenied = notificationPermission.permission === "denied";
  const generalWebPushAvailable = resolveGeneralWebPushAvailability(
    webPushAvailable,
    notificationPermission.permission,
  );
  const contextualKind = resolveContextualKind({
    browserPermissionDenied,
    hasInstallIntent: [
      Boolean(webPushInstallIntent),
      currentWebPushCapability?.kind === "supported",
    ].every(Boolean),
    hasPriceAlertRequest: Boolean(promptRequest),
  });

  const completeAllContextualRequests = useCallback(() => {
    completePromptRequest();
    completeInstallIntent();
  }, [completeInstallIntent, completePromptRequest]);
  const completeContextualRequest = useCallback(() => {
    const completion = resolveContextualRequestCompletion(contextualKind);
    if (completion === "install_intent") completeInstallIntent();
    if (completion === "price_alert_request") completePromptRequest();
  }, [completeInstallIntent, completePromptRequest, contextualKind]);

  useContextualRequestCleanup({
    browserPermissionDenied,
    browserPromptDismissed: browserPromptDismissal.dismissed,
    completeAllContextualRequests,
    completeInstallIntent,
    completePromptRequest,
    promptRequest,
    webPushJourneyAvailable,
    webPushInstallIntent,
    webPushLoading,
  });

  const availability = resolveMessagingPromptAvailability({
    allowGeneralPrompt,
    browserPromptDismissed: browserPromptDismissal.dismissed,
    browserPromptResolved: resolveBrowserPromptReady({
      capabilityResolved,
      dismissalResolved: browserPromptDismissal.isResolved,
      installIntentResolved,
      permissionResolved: notificationPermission.resolved,
    }),
    contextualKind,
    dismissed,
    emailAvailable: [viewer.isMember, !hasVerifiedEmail].every(Boolean),
    isResolved,
    telegramAuthAvailable,
    telegramConnected,
    telegramLoading,
    webPushAvailable: generalWebPushAvailable,
    webPushLoading: webPushLoading || !notificationPermission.resolved,
    whatsappAvailable: whatsappQuery.data?.available,
    whatsappLoading: whatsappQuery.isLoading,
  });
  if (!availability) return null;

  return (
    <Suspense fallback={null}>
      <MessagingPrompt
        browserPromptDismissForDays={browserPromptDismissal.dismissForDays}
        contextualKind={availability.contextualKind}
        contextualRequestKey={resolveContextualRequestKey(promptRequest, webPushInstallIntent)}
        contextualRequest={promptRequest}
        dismissForDays={dismissForDays}
        emailAvailable={availability.emailAvailable}
        hideGeneralOnMobile={hideGeneralOnMobile}
        isGuest={viewer.isGuest}
        onContextualRequestHandled={completeContextualRequest}
        telegramAvailable={availability.telegramAvailable}
        viewerType={viewer.identity}
        whatsappAvailable={availability.whatsappAvailable}
      />
    </Suspense>
  );
}
